/**
 * TURN HIS HEAD TOWARD THE BALL.
 *
 * Owner, filming a dribble: "he is not looking at the ball".
 *
 * ⚠ THIS IS A YAW PROBLEM AND IT WAS BEING TREATED AS A PITCH ONE. Three earlier
 * passes corrected the gaze ELEVATION in the clips — the run was gazing 24.7 deg
 * up against a Mixamo reference of 23.1 down, and that was real. But a ball
 * beside the player cannot be looked at by pitching down at all: it needs the
 * head to turn. No clip can carry that, because where the ball is relative to
 * the body is not a property of the animation. It has to be applied at runtime.
 *
 * So this runs AFTER the mixer has posed the skeleton and rotates the head bone
 * toward the ball, additively, on top of whatever the clip did.
 *
 * ── The three rules that keep it from looking worse than no look-at ──────────
 *
 * 1. CLAMPED. A human head turns roughly 70 deg off the shoulders and no
 *    further; past that you turn your body. Beyond the cone the head simply
 *    holds at the limit, which reads as "he can't see it" — correct, and far
 *    better than a head on backwards.
 * 2. EASED, NOT SNAPPED. The target is approached at a rate, so a ball that
 *    jumps across him (a tackle, a restart, a switch of play) is followed by a
 *    turn of the head rather than a cut. The rate is per-second and dt-scaled,
 *    so it is identical at 61 and 165 fps — the thing the whole session's other
 *    defects came down to.
 * 3. WEIGHTED BY PACE. At a sprint a footballer looks where he is GOING, not at
 *    his feet; at a walk he can watch the ball. The blend follows that, so the
 *    look-at fades out as he speeds up instead of dragging his head sideways at
 *    8 m/s.
 */
import { Object3D, Quaternion, Vector3 } from 'three';

/** How far the head may turn off the chest before it gives up and holds. */
export const HEAD_YAW_LIMIT_RAD = (70 * Math.PI) / 180;
/** And in pitch — much tighter, since the neck's own curve carries most of it. */
export const HEAD_PITCH_LIMIT_RAD = (28 * Math.PI) / 180;
/** Radians per second the head turns toward a new target. */
export const HEAD_TURN_RATE = 6.0;
/**
 * Ground speed at which the look-at has faded to nothing. Below `FULL` he
 * watches the ball; above `NONE` he watches the pitch ahead.
 */
export const LOOK_FULL_SPEED = 2.0;
export const LOOK_NONE_SPEED = 6.0;
/**
 * Horizontal ball distance at which the look-at fades to nothing at the NEAR
 * end. Below `NONE` a resting ball is close enough (a kickoff spot, a ball
 * parked at the toe) that even sub-centimetre position noise — ordinary
 * resting-contact solver jitter, see `ball-headrest.test.ts` for the same
 * class of near-field instability on the sim side — swings `dYaw` by several
 * degrees, because atan2's sensitivity to a fixed positional delta scales
 * with 1/distance. HEAD_TURN_RATE is fast enough to chase that every frame,
 * which reads as the head twitching on an otherwise motionless player. A real
 * player does not crane his neck to stare straight down at a ball touching
 * his own foot anyway, so fading to the clip's neutral pose here is right on
 * both counts: it kills the amplified noise and it is the more natural pose.
 */
export const LOOK_MIN_DIST_NONE = 0.4;
export const LOOK_MIN_DIST_FULL = 1.2;

const toBall = new Vector3();
const flat = new Vector3();

/** Per-slot easing state. One number; the head only ever eases in yaw-space. */
export interface HeadLookState {
  yaw: number;
  pitch: number;
}

export function createHeadLookState(): HeadLookState {
  return { yaw: 0, pitch: 0 };
}

/**
 * Rotate `head` toward the ball. Call once per rendered frame, AFTER the mixer
 * has advanced and BEFORE world matrices are computed for this subtree.
 *
 * `bodyYaw` is the sim's facing (yaw measured +X toward +Z, the sim convention).
 */
export function applyHeadLookAt(
  head: Object3D,
  st: HeadLookState,
  bodyYaw: number,
  headWorld: Vector3,
  ballX: number,
  ballY: number,
  ballZ: number,
  speed: number,
  dt: number,
): void {
  // Weight first — if he is sprinting there is nothing to compute.
  const w =
    speed <= LOOK_FULL_SPEED ? 1
    : speed >= LOOK_NONE_SPEED ? 0
    : 1 - (speed - LOOK_FULL_SPEED) / (LOOK_NONE_SPEED - LOOK_FULL_SPEED);

  toBall.set(ballX - headWorld.x, ballY - headWorld.y, ballZ - headWorld.z);
  flat.set(toBall.x, 0, toBall.z);
  const flatLen = flat.length();
  if (flatLen < 1e-4) return;

  // Near-field fade — see LOOK_MIN_DIST_NONE's comment. Computed on the SAME
  // flatLen the angle below is about to divide by, so it is exactly the
  // distance the amplification depends on.
  const distFade =
    flatLen >= LOOK_MIN_DIST_FULL ? 1
    : flatLen <= LOOK_MIN_DIST_NONE ? 0
    : (flatLen - LOOK_MIN_DIST_NONE) / (LOOK_MIN_DIST_FULL - LOOK_MIN_DIST_NONE);

  // Bearing to the ball RELATIVE TO THE CHEST, wrapped to +-pi. The sim's yaw 0
  // faces +X, so the body's forward bearing is `bodyYaw` in the same atan2 frame.
  let dYaw = Math.atan2(toBall.z, toBall.x) - bodyYaw;
  while (dYaw > Math.PI) dYaw -= 2 * Math.PI;
  while (dYaw < -Math.PI) dYaw += 2 * Math.PI;
  const dPitch = Math.atan2(toBall.y, flatLen);

  const wd = w * distFade;
  const tgtYaw = Math.max(-HEAD_YAW_LIMIT_RAD, Math.min(HEAD_YAW_LIMIT_RAD, dYaw)) * wd;
  const tgtPitch = Math.max(-HEAD_PITCH_LIMIT_RAD, Math.min(HEAD_PITCH_LIMIT_RAD, dPitch)) * wd;

  // Ease toward it at a fixed angular rate — dt-scaled, so frame rate cannot
  // change how fast his head turns.
  const step = HEAD_TURN_RATE * dt;
  st.yaw += Math.max(-step, Math.min(step, tgtYaw - st.yaw));
  st.pitch += Math.max(-step, Math.min(step, tgtPitch - st.pitch));

  if (Math.abs(st.yaw) < 1e-4 && Math.abs(st.pitch) < 1e-4) return;

  // ⚠ POST-MULTIPLIED, so it lands in the head bone's OWN frame and composes
  // with whatever the clip posed rather than replacing it. Pre-multiplying would
  // apply the turn in the neck's frame and the head would swing as the spine
  // moved. The bone's local Y runs up through the skull (yaw) and local X is the
  // side axis (pitch) — the same axes the clip corrections were probed on.
  head.quaternion.multiply(
    SCRATCH.setFromAxisAngle(LOCAL_UP, st.yaw).multiply(
      SCRATCH2.setFromAxisAngle(LOCAL_SIDE, st.pitch),
    ),
  );
}

/** The head bone's own axes: Y runs up through the skull, X is the side axis. */
const LOCAL_UP = new Vector3(0, 1, 0);
const LOCAL_SIDE = new Vector3(1, 0, 0);
const SCRATCH = new Quaternion();
const SCRATCH2 = new Quaternion();
